import React, { useRef, useEffect } from 'react';
import { theme } from '../theme';
import { REASON_CODES } from '../categories';
import { FORM_WORD } from '../words';
import type { FlagId, TicketFacets, SenderOrganisationFacet, AttributeFacetValue } from '../serverTypes';
import { useCategoryMeta } from '../context/ClassificationSchemaContext';
import { pillFace } from './SecondaryAxisPills';
import { FLAG_DOMINANCE_ORDER } from './FlagPill';
import { DateRangeInput, DateRangeValue } from './DateRangeInput';
import { TICKET_ATTRIBUTES, type AttributeFilter } from '../services/ticketAttributes';
import { attributeValueStandIn, PRIMARY_SWATCH_BORDER_WIDTH, type PrimarySwatch } from '../services/categoryMeta';
import { flagImpliedBy } from '../services/impliedSignals';
import { SearchIcon, FilterSlidersIcon } from './icons';
import { SENDER_TOKEN_PRUEFEN_COLOR } from './SenderPill';

// ─── FilterPanel ─────────────────────────────────────────
// <purpose>Reusable search + filter dropdown. Controlled — parent owns all state.</purpose>
// <dependencies>theme, REASON_CODES from constants; useCategoryMeta (role-aware category set)</dependencies>
// <constraints>Click-outside closes dropdown. Live filtering (no apply button). Relative positioning.</constraints>
interface FilterPanelProps {
  search: string;
  onSearchChange: (v: string) => void;
  open: boolean;
  onOpenChange: (v: boolean) => void;

  // Optional sections — rendered only when callback provided
  timeFilter?: string | null;
  onTimeFilterChange?: (v: string | null) => void;
  categoryFilter?: string | null;
  onCategoryFilterChange?: (v: string | null) => void;
  reasonCodeFilter?: string[];
  onReasonCodeFilterChange?: (codes: string[]) => void;
  // Classified-alongside ATTRIBUTE sections (wim vocabulary v7, 2026-09-13). Keyed by SERVED
  // attribute name (snake_case, exactly as the vocabulary spells it) — the same key the section
  // heading and every bubble resolve through, so nothing re-keys a selection on its way to the
  // page's own state. One callback for ALL sections rather than one per attribute: how many
  // sections exist is the tenant's decision, and a per-attribute prop would put that decision in
  // this interface.
  attributeFilter?: AttributeFilter;
  onAttributeFilterChange?: (name: string, values: string[]) => void;
  // part 7, 2026-10-11 (spec 16.5): every pill kind is a facet, worded as the pill is. The
  // „Warnhinweis" section (the flag pills; counts from facets.flags) follows the optional-section
  // convention: rendered only when the callback is wired AND the tenant serves the words.
  flagFilter?: FlagId[];
  onFlagFilterChange?: (ids: FlagId[]) => void;
  // The „Formular" facet (part 7 fix loop, 2026-10-12): the form pill's own word as ONE bubble,
  // rendered only when the callback is wired AND the tenant serves form extraction
  // (cm.formExtractionEnabled — never a role id). Its count is the visible bucket's served
  // form-bearing subset, already resolved by the page (formFilterCountOf below).
  hasFormFilter?: boolean;
  onHasFormFilterChange?: (on: boolean) => void;
  formFilterCount?: FormFilterCount;
  // Sender-identity registry (P2, 2026-09-13), Task R2 — the „Absender" organisation section.
  // Same optional-section convention as every other block here: rendered only when the caller
  // wires onSenderFilterChange AND the vocabulary declares sender_kind (see the section itself).
  // `facets` backs BOTH this section (its org row set + counts) and the count span every generic
  // attribute bubble above now carries — one prop rather than two, since a page that fetches one
  // fetches the other in the same response.
  senderFilter?: string[];
  onSenderFilterChange?: (ids: string[]) => void;
  facets?: TicketFacets | null;
  // Set by useTicketFacets once a facets read has FAILED (a backend with no /tickets/facets, most
  // likely). Every count span then renders nothing at all rather than a „–" that will never resolve
  // — one reflow, at the first failure, instead of a permanent placeholder (final review, item 4).
  facetsUnsupported?: boolean;
  uploaders?: string[];
  filterUser?: string;
  onFilterUserChange?: (v: string) => void;
  filterDateRange?: string;
  onFilterDateRangeChange?: (v: string) => void;
  dateRangeLabel?: string;

  // Server-side ISO date-range picker (Task 5 / ArchivSeite). Additive: when
  // onDateRangeChange is provided the DateRangeInput renders INSTEAD of the
  // free-text filterDateRange field.
  from?: string | null;
  to?: string | null;
  onDateRangeChange?: (range: DateRangeValue) => void;

  onClearAll: () => void;
  hasActiveFilters: boolean;
}

const TIME_OPTIONS = [
  { label: '7 Tage', value: '7d' },
  { label: '30 Tage', value: '30d' },
  { label: 'Dieser Monat', value: 'thisMonth' },
] as const;

// `swatch` (part 7 fix loop, 2026-10-12): the UNCLASSIFIED category's bubble wears its primary swatch
// (categoryMeta.resolvePrimarySwatch -- the slate tint; slate fill + white ink when active) even when inactive, so it
// reads as a failed classification, not another category. Padding gives back what the heavier border
// takes (PRIMARY_SWATCH_BORDER_WIDTH over the plain 1px), so every bubble keeps one box.
const swatchBorderExtra = parseFloat(PRIMARY_SWATCH_BORDER_WIDTH) - 1;
const bubbleStyle = (active: boolean, swatch: PrimarySwatch | null = null): React.CSSProperties => ({
  padding: swatch ? `${3 - swatchBorderExtra}px ${10 - swatchBorderExtra}px` : '3px 10px', fontSize: 11, fontWeight: active || swatch ? 600 : 400,
  color: active ? (swatch ? swatch.activeInk : theme.colors.white) : swatch ? swatch.ink : theme.colors.navyMuted,
  backgroundColor: active ? (swatch ? swatch.activeFill : theme.colors.navy) : swatch ? swatch.fill : 'transparent',
  border: swatch ? (active ? `${PRIMARY_SWATCH_BORDER_WIDTH} solid ${swatch.activeFill}` : swatch.border) : `1px solid ${active ? theme.colors.navy : theme.colors.borderLight}`,
  borderRadius: 20, cursor: 'pointer', fontFamily: theme.font.family,
  transition: 'all 0.15s ease', outline: 'none', userSelect: 'none' as const,
});

const filterLabelStyle: React.CSSProperties = {
  fontSize: 11, color: theme.colors.textSecondary, display: 'block',
  marginBottom: 6, fontFamily: theme.font.family,
};

const filterInputStyle: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box' as const,
  padding: `${theme.spacing.xs}px ${theme.spacing.sm}px`,
  border: `1px solid ${theme.colors.borderLight}`, borderRadius: theme.borderRadius.sm,
  fontSize: 13, fontFamily: theme.font.family, outline: 'none',
  backgroundColor: theme.colors.white,
};

// The count span every countable filter bubble carries (attribute bubbles AND the organisation
// section below) — `–` while the facet answer for this bubble hasn't loaded yet, the served number
// once it has. ALWAYS rendered (never conditionally omitted): the alternative — no span until a
// count exists — is the layout shift the bubble register exists to prevent (UI Layout Stability).
// `unsupported` (final review, item 4) is the ONE case that renders no span at all: the backend
// answered no facets and never will this session, so „–" would state "still loading" forever.
const FacetCount: React.FC<{ count: number | undefined; unsupported?: boolean }> = ({ count, unsupported }) => {
  if (unsupported) return null;
  return <span style={{ opacity: 0.7, marginLeft: 4 }}>{count === undefined ? '–' : count}</span>;
};

// The „Formular" bubble's count, as a page resolves it from its list counts (part 7 fix loop,
// 2026-10-12): a NUMBER once the bucket's form-bearing subset is served; 'pending' while the first
// list answer is still out (the bubble pre-allocates the „–" every count span shows while loading);
// 'unserved' when the answer came without one (an older core) — then the bubble carries NO count
// span, never a fabricated 0.
export type FormFilterCount = number | 'pending' | 'unserved';
export function formFilterCountOf<B extends string>(
  formBearing: Partial<Record<B, number>> | undefined,
  bucket: B,
  loading: boolean,
): FormFilterCount {
  const n = formBearing?.[bucket];
  if (typeof n === 'number') return n;
  return loading ? 'pending' : 'unserved';
}

// The one place a bubble's FACE text sits in its own element rather than as the button's bare text
// node — required so a FacetCount sibling never changes what `getByText(face)` matches (the
// button's OWN textContent would otherwise become "Face<count>", no longer equal to the face
// alone). Shared by the attribute bubbles below and SenderOrganisationFilterSection's own bubbles.
const FacetLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => <span>{children}</span>;

// The organisation bubble's own style: the interactive chip register (bubbleStyle) with the
// registry's review-state colour (SENDER_TOKEN_PRUEFEN_COLOR, ruling R14 — the sender-identity
// registry's own amber, distinct from the K4-exclusive orange and the wim `pruefen` handling hue)
// standing in for the border on a PROPOSED match, dashed so
// it reads as "needs a decision" rather than "selected". Deliberately NOT gated on `active`: a
// clerk may select a proposed org to filter by it, and the review cue must survive that click.
const senderOrgBubbleStyle = (active: boolean, proposed: boolean): React.CSSProperties => ({
  ...bubbleStyle(active),
  ...(proposed ? { border: `1px dashed ${SENDER_TOKEN_PRUEFEN_COLOR}` } : {}),
});

// The kind heading's own style (part 7, 2026-10-11): a heading that is also a toggle. Smaller and
// quieter than an organisation bubble so the two registers stay apart, with the SAME border width
// in both states (transparent when off) so pressing it never moves a pixel.
const senderKindHeadingStyle = (active: boolean): React.CSSProperties => ({
  display: 'inline-flex', alignItems: 'center',
  padding: '1px 8px', marginBottom: 3, fontSize: 10, fontWeight: 600,
  color: active ? theme.colors.white : theme.colors.textSecondary,
  backgroundColor: active ? theme.colors.navy : 'transparent',
  border: `1px solid ${active ? theme.colors.navy : 'transparent'}`,
  borderRadius: 20, cursor: 'pointer', fontFamily: theme.font.family,
  transition: 'all 0.15s ease', outline: 'none', userSelect: 'none' as const,
});

// ─── SenderOrganisationFilterSection ────────────────────
// <purpose>THE „Absender" filter — one section for the one sender pill („Organisation · Art", part 7,
// 2026-10-11). Its KIND HEADINGS are the sender_kind facet (clickable: they set
// `attributeFilter.sender_kind`, the existing `senderKind` param — sender_kind has no section of its
// own any more), and under each heading sit that kind's matched organisations (facets
// .senderOrganisations), each face `shortName ?? name` exactly as the pill wears it.</purpose>
// <dependencies>useCategoryMeta().attribute('sender_kind') for the kind ORDER (its declared
// `values`) and .attributeFace for each kind's word; TicketFacets.senderOrganisations for the org
// row set + counts and TicketFacets.attributes.sender_kind for the heading counts, both computed
// server-side with their own dimension released.</dependencies>
// <constraints>Every DECLARED kind gets a heading — the filter register: a kind whose served face is
// `null` (no word on the pill) is still filterable, headed by the propercased stand-in, the same
// fallback every filter bubble uses (categoryMeta.attributeValueStandIn). A PROPOSED organisation
// sits under its own kind with the dashed review border (per-row, status === 'proposed'); part 7
// deleted the trailing review group that used to collect them. An organisation of a kind the
// vocabulary never declares is listed after the headed groups WITHOUT a heading — never under an
// invented one, never dropped. Headings are toggles only when the page wires the attribute filter;
// organisations are listed only when it wires the organisation filter.</constraints>
const SenderOrganisationFilterSection: React.FC<{
  organisations: SenderOrganisationFacet[];
  kindOrder: string[];
  faceFor: (kind: string) => string;
  kindCounts: AttributeFacetValue[] | undefined;
  facetsLoaded: boolean;
  selectedKinds: string[];
  onKindsChange?: (kinds: string[]) => void;
  selected: string[];
  onChange?: (ids: string[]) => void;
  facetsUnsupported?: boolean;
}> = ({ organisations, kindOrder, faceFor, kindCounts, facetsLoaded, selectedKinds, onKindsChange, selected, onChange, facetsUnsupported }) => {
  const byKind = new Map<string, SenderOrganisationFacet[]>();
  const unheaded: SenderOrganisationFacet[] = [];
  if (onChange) {
    for (const org of organisations) {
      if (!kindOrder.includes(org.kind)) { unheaded.push(org); continue; }
      const list = byKind.get(org.kind) ?? [];
      list.push(org);
      byKind.set(org.kind, list);
    }
  }

  const toggle = (id: string) =>
    onChange?.(selected.includes(id) ? selected.filter(v => v !== id) : [...selected, id]);
  const toggleKind = (kind: string) =>
    onKindsChange?.(selectedKinds.includes(kind) ? selectedKinds.filter(v => v !== kind) : [...selectedKinds, kind]);

  // A kind's organisations form a group NAMED by the kind's face; the unheaded remainder (kinds no
  // vocabulary declares) has no name to give, so it is a plain row inside the „Absender" group
  // rather than an unnamed group (final review, part 7, 2026-10-11).
  const orgRow = (orgs: SenderOrganisationFacet[], name?: string) => (
    <div role={name ? 'group' : undefined} aria-label={name} style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {orgs.map(org => {
        const active = selected.includes(org.id);
        const proposed = org.status === 'proposed';
        return (
          <button key={org.id} type="button" style={senderOrgBubbleStyle(active, proposed)}
            title={org.name} onClick={() => toggle(org.id)}>
            <FacetLabel>{org.shortName ?? org.name}</FacetLabel>
            <FacetCount count={org.count} unsupported={facetsUnsupported} />
          </button>
        );
      })}
    </div>
  );

  // a11y: the section is itself a `group` labelled „Absender", so each kind's organisation group is
  // nested under a parent that names what the kinds divide.
  return (
    <div role="group" aria-label="Absender" style={{ marginBottom: theme.spacing.sm }}>
      <span style={filterLabelStyle}>Absender</span>
      {kindOrder.map(kind => {
        const face = faceFor(kind);
        const orgs = byKind.get(kind) ?? [];
        const active = selectedKinds.includes(kind);
        const count = facetsLoaded ? (kindCounts?.find(v => v.value === kind)?.count ?? 0) : undefined;
        return (
          <div key={kind} style={{ marginBottom: 4 }}>
            {onKindsChange ? (
              <button type="button" aria-pressed={active} style={senderKindHeadingStyle(active)}
                title={face} onClick={() => toggleKind(kind)}>
                <FacetLabel>{face}</FacetLabel>
                <FacetCount count={count} unsupported={facetsUnsupported} />
              </button>
            ) : (
              <span style={{ ...filterLabelStyle, fontSize: 10, marginBottom: 3 }}>{face}</span>
            )}
            {orgs.length > 0 && orgRow(orgs, face)}
          </div>
        );
      })}
      {unheaded.length > 0 && orgRow(unheaded)}
    </div>
  );
};

export const FilterPanel: React.FC<FilterPanelProps> = (props) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { open, onOpenChange } = props;
  const cm = useCategoryMeta();

  // Why: close dropdown when clicking anywhere outside the FilterPanel wrapper
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        onOpenChange(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onOpenChange]);

  // ONE section per attribute that is BOTH bound by this frontend (TICKET_ATTRIBUTES — a filter the
  // list API has no param for would narrow nothing) and actually declared by the tenant. Three
  // further conditions, each a real state:
  //   - enum only: a scalar or a boolean (the reminder flag) has no bubble row to offer,
  //     and guessing a control for an unknown type is how a filter for a value space nobody declared
  //     appears on screen.
  //   - a German heading: the section is titled by the served labelDe and nothing invents one, so an
  //     attribute declared without it renders no section (its PILLS are unaffected).
  //   - at least one offerable value: an enum whose every value is suppressed has nothing to say
  //     here, while a suppressed value BESIDE an offered one still gets its bubble (see the face
  //     fallback at the bubble itself).
  const attributeSections = props.onAttributeFilterChange
    ? TICKET_ATTRIBUTES.flatMap(binding => {
        // sender_kind is the „Absender" section's kind headings (part 7, 2026-10-11: one section for
        // the one sender pill), never a section of its own.
        if (binding.name === 'sender_kind') return [];
        const attr = cm.attribute(binding.name);
        if (!attr || attr.type !== 'enum' || !attr.labelDe) return [];
        if (!(attr.values ?? []).some(value => cm.attributeFace(attr.name, value) !== null)) return [];
        return [attr];
      })
    : [];

  // The „Absender" organisation section (Task R2) — gated on BOTH halves the way every other
  // optional section here is: the CALLBACK (does this page own the state to filter by) and the
  // DECLARATION (does this tenant's vocabulary even have a sender_kind attribute to group by). A
  // vocabulary with no sender_kind has no kind to label a group with, so there is nothing honest
  // this section could render.
  // part 7, 2026-10-11: EITHER callback opens it — the kind headings are the sender_kind attribute
  // filter, the organisations the registry filter; a page wiring only one still gets that half.
  const senderKindAttr = cm.attribute('sender_kind');
  const showSenderSection = !!(props.onSenderFilterChange || props.onAttributeFilterChange) && !!senderKindAttr;

  // „Warnhinweis" (part 7, 2026-10-11): one bubble per flag the tenant WORDS (servedWord — no raw-key
  // floor: a flag nobody worded is not offered), in the pill's dominance order.
  // THE implication rule in the filter (part 7 fix loop, 2026-10-12): a „Warnhinweis" bubble whose
  // flag a CATEGORY implies (a served implied landing on one of this tenant's primary values) is NOT
  // offered while the category section is — the category bubble stands for it, with the category's
  // own count. The flag is not lost: it still COUNTS in the facet totals (facets.flags is served and
  // untouched here), and its rows stay FILTERABLE through that category bubble, because an implied
  // landing means every row on the category carries the flag. Every other bubble is unchanged. A
  // bubble the clerk has already selected stays offered, so a held selection can always be released.
  const flagImpliedByOfferedPrimary = (id: FlagId): boolean => {
    const by = flagImpliedBy(cm, id);
    return !!props.onCategoryFilterChange && by !== null && cm.categoryKeys.includes(by)
      && !(props.flagFilter ?? []).includes(id);
  };
  const flagOptions = props.onFlagFilterChange
    ? FLAG_DOMINANCE_ORDER.flatMap(id => {
        const word = cm.servedWord('flag', id);
        return word === null || flagImpliedByOfferedPrimary(id) ? [] : [{ id, word }];
      })
    : [];
  // „Formular" (part 7 fix loop, 2026-10-12 — every pill kind is a facet, worded as the pill is):
  // offered only where the tenant SERVES form extraction, the same capability that gates the pill.
  const showFormSection = !!props.onHasFormFilterChange && cm.formExtractionEnabled;

  const hasDropdownSections = !!(
    props.onTimeFilterChange || props.onCategoryFilterChange ||
    props.onReasonCodeFilterChange || props.onFilterUserChange ||
    props.onFilterDateRangeChange || props.onDateRangeChange
  ) || attributeSections.length > 0 || showSenderSection || flagOptions.length > 0 || showFormSection;

  return (
    // `position: relative` makes this wrapper the positioning context the expanded body hangs from
    // (operator ruling 2026-09-16). Only the SEARCH BAR is in the flow here; the sections open as an
    // out-of-flow layer below, so the list underneath keeps its top edge whatever the filter does.
    <div
      ref={wrapperRef}
      data-testid="filter-panel-wrapper"
      style={{ position: 'relative', padding: `${theme.spacing.sm}px ${theme.spacing.md}px` }}
    >
      {/* Search bar */}
      <div style={{
        display: 'flex', alignItems: 'center',
        border: `1px solid ${theme.colors.borderLight}`,
        borderRadius: theme.borderRadius.md,
        backgroundColor: theme.colors.bgPage,
        padding: `0 ${theme.spacing.sm}px`,
      }}>
        <SearchIcon size={16} color={theme.colors.textSecondary} />
        <input
          id="filter-search"
          name="filter-search"
          type="text"
          value={props.search}
          onChange={e => props.onSearchChange(e.target.value)}
          placeholder="Suchen..."
          style={{
            flex: 1, border: 'none', background: 'none', outline: 'none',
            padding: `${theme.spacing.sm}px ${theme.spacing.sm}px`,
            fontSize: 13, fontFamily: theme.font.family, color: theme.colors.navy,
          }}
        />
        {hasDropdownSections && (
          <button
            onClick={() => props.onOpenChange(!props.open)}
            aria-label={props.open ? 'Filter schließen' : 'Filter öffnen'}
            aria-expanded={props.open}
            style={{
              width: 40, height: 40, padding: 0,
              background: 'none', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              opacity: (props.open || props.hasActiveFilters) ? 1 : 0.5,
            }}
          >
            <FilterSlidersIcon size={14} active={props.hasActiveFilters} color={props.hasActiveFilters ? theme.colors.navy : theme.colors.textSecondary} />
          </button>
        )}
        {props.hasActiveFilters && (
          <button
            onClick={props.onClearAll}
            aria-label="Alle Filter zurücksetzen"
            style={{
              padding: 4, background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 14, color: theme.colors.textSecondary,
              display: 'flex', alignItems: 'center',
            }}
          >
            &times;
          </button>
        )}
      </div>

      {/* Filter dropdown — an OVERLAY, not a block in the flow (operator ruling 2026-09-16).
          `top: 100%` hangs it from the wrapper's bottom edge, left/right 0 give it the wrapper's
          full width, and the shadow plus the opaque page-white ground make it read as a layer over
          the rows rather than a part of the list.
          It is MOUNTED ONLY WHILE OPEN and fades in, rather than living there permanently at
          opacity 0: an always-mounted panel would put its inputs in the tab order and in the
          screen-reader's path while the clerk believes it is shut. Nothing shifts either way —
          being out of flow, it has no layout box to pre-allocate. */}
      {props.open && hasDropdownSections && (
        <div data-testid="filter-panel-body" style={{
          position: 'absolute',
          left: 0, right: 0, top: '100%',
          zIndex: theme.zIndex.popover,
          padding: theme.spacing.md,
          backgroundColor: theme.colors.white,
          borderRadius: theme.borderRadius.md,
          boxShadow: theme.shadows.cardHover,
          border: `1px solid ${theme.colors.borderLight}`,
          animation: 'stx-fadeIn 0.12s ease',
        }}>
          {/* Time bubbles */}
          {props.onTimeFilterChange && (
            <div style={{ marginBottom: theme.spacing.sm }}>
              <span id="filter-zeitraum-label" style={filterLabelStyle}>Zeitraum</span>
              <div role="group" aria-labelledby="filter-zeitraum-label" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {TIME_OPTIONS.map(f => {
                  const active = props.timeFilter === f.value;
                  return (
                    <button key={f.value} style={bubbleStyle(active)}
                      onClick={() => props.onTimeFilterChange!(active ? null : f.value)}>
                      {f.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Category bubbles — role-aware: VNB marktprozess (abbr chip) / gMSB K-codes */}
          {props.onCategoryFilterChange && (
            <div style={{ marginBottom: theme.spacing.sm }}>
              <span id="filter-kategorie-label" style={filterLabelStyle}>{cm.categoryNoun}</span>
              <div role="group" aria-labelledby="filter-kategorie-label" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {/* categoryKeys (part 7, 2026-10-11 — was performanceCategoryKeys): every served
                    value INCLUDING the unclassified one. The unclassified pill is something a clerk
                    recognises on a row, so it is something they can find here; the performance
                    split stays the stats surfaces' question, not the filter's. */}
                {cm.categoryKeys.map(cat => {
                  const active = props.categoryFilter === cat;
                  return (
                    <button key={cat} style={bubbleStyle(active, cm.isUnclassified(cat) ? cm.swatch(cat) : null)}
                      title={cm.label(cat)}
                      onClick={() => props.onCategoryFilterChange!(active ? null : cat)}>
                      {cm.categoryLabelsFromData ? cm.label(cat) : cat}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* „Warnhinweis" (part 7, 2026-10-11) — the flag pills as a facet: served words in dominance
              order, each with its facet count (facets.flags, enforced flags only, own dimension
              released). OR within the section, like the secondary axis below. */}
          {flagOptions.length > 0 && (
            <div style={{ marginBottom: theme.spacing.sm }}>
              <span id="filter-flag-label" style={filterLabelStyle}>Warnhinweis</span>
              <div role="group" aria-labelledby="filter-flag-label" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {flagOptions.map(({ id, word }) => {
                  const selected = props.flagFilter ?? [];
                  const active = selected.includes(id);
                  // review round 1: counts only when the server SERVES the dimension. A loaded
                  // facets answer without `flags` (an older core) renders no count span — a settled
                  // „0" there would be a number nobody counted. A served list reads a missing flag
                  // as a real 0, like every other facet.
                  const flagCounts = props.facets?.flags;
                  const count = flagCounts ? (flagCounts.find(v => v.value === id)?.count ?? 0) : undefined;
                  const countUnserved = !!props.facets && flagCounts === undefined;
                  return (
                    <button key={id} type="button" style={bubbleStyle(active)} aria-pressed={active} title={word}
                      onClick={() => props.onFlagFilterChange!(active ? selected.filter(v => v !== id) : [...selected, id])}>
                      <FacetLabel>{word}</FacetLabel>
                      <FacetCount count={count} unsupported={props.facetsUnsupported || countUnserved} />
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* „Anhang" (part 7 fix loop, 2026-10-12) — the „Formular" pill as a facet: ONE bubble, the
              pill's own word (constants.FORM_WORD), `hasForm=true` on the list, the facets and the
              Excel selection. Its count is the visible bucket's served form-bearing subset; no
              span at all where the list does not serve one (FormFilterCount 'unserved'). */}
          {showFormSection && (
            <div style={{ marginBottom: theme.spacing.sm }}>
              <span id="filter-form-label" style={filterLabelStyle}>Anhang</span>
              <div role="group" aria-labelledby="filter-form-label" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                <button type="button" style={bubbleStyle(!!props.hasFormFilter)} aria-pressed={!!props.hasFormFilter}
                  onClick={() => props.onHasFormFilterChange!(!props.hasFormFilter)}>
                  <FacetLabel>{FORM_WORD}</FacetLabel>
                  <FacetCount
                    count={typeof props.formFilterCount === 'number' ? props.formFilterCount : undefined}
                    unsupported={props.formFilterCount === undefined || props.formFilterCount === 'unserved'} />
                </button>
              </div>
            </div>
          )}

          {/* Second-axis multi-select — role-aware: VNB anliegen (schema labels) / gMSB reason codes.
              An interactive filter OPTION (selectable, own active state), not a display pill — it
              reuses the secondary-axis resolution accessors (secondaryAxisText/secondaryAxisHover)
              rather than SecondaryAxisPills, which owns markup/click-handling this affordance keeps
              for itself; the FACE still goes through pillFace('axis2', …) so the bubble and the row
              pill never case one code two ways on one screen (walk round 4, 2026-09-08), while the
              title keeps the served casing like every axis2 hover. categoryLabelsFromData still
              decides the ID SET (secondaryAxisKeys floors to [] with no vocabulary/schema, so
              gMSB's floor needs REASON_CODES as its option source) — only the TEXT formula
              collapsed to one, since both accessors resolve any code regardless of source. */}
          {props.onReasonCodeFilterChange && (
            <div style={{ marginBottom: theme.spacing.sm }}>
              <span id="filter-grundcode-label" style={filterLabelStyle}>{cm.secondaryAxisNoun}</span>
              <div role="group" aria-labelledby="filter-grundcode-label" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {(cm.categoryLabelsFromData ? cm.secondaryAxisKeys : REASON_CODES.map(rc => rc.code))
                  .map(id => ({ value: id, text: pillFace('axis2', cm.secondaryAxisText(id)), title: cm.secondaryAxisHover(id) }))
                  .map(opt => {
                  const codes = props.reasonCodeFilter ?? [];
                  const active = codes.includes(opt.value);
                  return (
                    <button key={opt.value} style={bubbleStyle(active)}
                      title={opt.title}
                      onClick={() => {
                        const next = active
                          ? codes.filter(c => c !== opt.value)
                          : [...codes, opt.value];
                        props.onReasonCodeFilterChange!(next);
                      }}>
                      {opt.text}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Attribute sections (wim vocabulary v7, 2026-09-13) — data-driven: which sections exist,
              which values they offer and what each is called all come from the served vocabulary,
              so this block spells no commodity and no sender kind. Same interactive-chip boundary
              the secondary-axis section above draws (own markup + active state, resolution
              accessors only), and placed after it deliberately: a clerk narrows by classification
              first, then by what the mail is about, then by who/when. Each bubble carries a
              FacetCount (Task R2, sender-identity registry) — `undefined` (the loading placeholder)
              while `facets` hasn't arrived yet, else the served count or 0 for a value the facet
              answer is silent on (a settled facets object, zero matches). */}
          {attributeSections.map(attr => {
            const selected = props.attributeFilter?.[attr.name] ?? [];
            const labelId = `filter-attribute-${attr.name}-label`;
            const facetValues = props.facets?.attributes?.[attr.name];
            return (
              <div key={attr.name} style={{ marginBottom: theme.spacing.sm }}>
                <span id={labelId} style={filterLabelStyle}>{attr.labelDe}</span>
                <div role="group" aria-labelledby={labelId} style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {(attr.values ?? []).map(value => {
                    // THE one place the pill register and the filter register part company: a served
                    // null face means "no pill on a row", which says nothing about whether a clerk
                    // may filter for it — so the bubble falls to the same propercased stand-in an
                    // undeclared value gets (categoryMeta's attributeValueStandIn, shared with the
                    // resolver so there is one propercase in the app). The face itself still comes
                    // from cm.attributeFace: a tenant that words a value keeps its word here too.
                    const face = cm.attributeFace(attr.name, value) ?? attributeValueStandIn(value);
                    const active = selected.includes(value);
                    const count = props.facets
                      ? (facetValues?.find(v => v.value === value)?.count ?? 0)
                      : undefined;
                    return (
                      <button key={value} style={bubbleStyle(active)}
                        title={face}
                        onClick={() => props.onAttributeFilterChange!(
                          attr.name,
                          active ? selected.filter(v => v !== value) : [...selected, value],
                        )}>
                        <FacetLabel>{face}</FacetLabel>
                        <FacetCount count={count} unsupported={props.facetsUnsupported} />
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* „Absender" — ONE section for the one sender pill (part 7, 2026-10-11): kind headings
              filter the kind, the organisations under them filter the organisation. See
              SenderOrganisationFilterSection's own header for the grouping rules. */}
          {showSenderSection && (
            <SenderOrganisationFilterSection
              organisations={props.facets?.senderOrganisations ?? []}
              kindOrder={senderKindAttr!.values ?? []}
              faceFor={value => cm.attributeFace('sender_kind', value) ?? attributeValueStandIn(value)}
              kindCounts={props.facets?.attributes?.sender_kind}
              facetsLoaded={!!props.facets}
              selectedKinds={props.attributeFilter?.sender_kind ?? []}
              onKindsChange={props.onAttributeFilterChange
                ? kinds => props.onAttributeFilterChange!('sender_kind', kinds)
                : undefined}
              selected={props.senderFilter ?? []}
              onChange={props.onSenderFilterChange}
              facetsUnsupported={props.facetsUnsupported}
            />
          )}

          {/* Uploader + date range row */}
          {(props.onFilterUserChange || props.onFilterDateRangeChange) && (
            <div style={{ display: 'flex', gap: theme.spacing.md, flexWrap: 'wrap' }}>
              {props.onFilterUserChange && (
                <div style={{ flex: 1, minWidth: 140 }}>
                  <label htmlFor="filter-uploader" style={filterLabelStyle}>Hochgeladen von</label>
                  <select
                    id="filter-uploader"
                    value={props.filterUser ?? ''}
                    onChange={e => props.onFilterUserChange!(e.target.value)}
                    style={filterInputStyle}
                  >
                    <option value="">Alle</option>
                    {(props.uploaders ?? []).map(email => (
                      <option key={email} value={email}>{email}</option>
                    ))}
                  </select>
                </div>
              )}
              {/* Legacy free-text range. Suppressed when the ISO picker is wired. */}
              {props.onFilterDateRangeChange && !props.onDateRangeChange && (
                <div style={{ flex: 1, minWidth: 140 }}>
                  <label style={filterLabelStyle}>{props.dateRangeLabel ?? 'Zeitraum'}</label>
                  <input
                    id="filter-daterange-text"
                    name="filter-daterange-text"
                    type="text"
                    value={props.filterDateRange ?? ''}
                    onChange={e => props.onFilterDateRangeChange!(e.target.value)}
                    placeholder="z.B. 01.01 - 31.01"
                    style={filterInputStyle}
                  />
                </div>
              )}
            </div>
          )}

          {/* ISO date-range picker (ArchivSeite server-side from/to). Own full-width
              block — the native Von/Bis pair needs more room than the free-text field. */}
          {props.onDateRangeChange && (
            <div style={{ marginTop: theme.spacing.sm }}>
              <DateRangeInput
                from={props.from ?? null}
                to={props.to ?? null}
                onChange={props.onDateRangeChange}
                label={props.dateRangeLabel ?? 'Zeitraum'}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
